import { useState } from 'react';
import { Expenditure, EditableExpenditure } from '../hooks/useExpenditures';
import { PanelBase } from './PanelBase';
import { TableViewBase } from './TableViewBase';
import { amountForPeriod, Period } from '../utils';
import { showSuccess, showError } from 'src/toasts/toasts';
import { ExpenditureModal } from './ExpenditureModal';
import { useDisclosure } from '@mantine/hooks';
import { frequencyAmountCell } from '../components/cashflowCells';
import { truncatedTextOrNotRecorded } from '../notRecorded';
import { useCurrency } from 'src/common/hooks/useCurrency';

type ExpenditurePanelProps = {
  items: Expenditure[];
  onSave: (data: EditableExpenditure[]) => Promise<void>;
  onDelete: (ids: string[]) => Promise<void>;
  period: Period;
};

export function ExpenditurePanel({ items = [], onSave, onDelete, period }: ExpenditurePanelProps) {
  const currency = useCurrency();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedExpenditure, setSelectedExpenditure] = useState<Expenditure | undefined>();

  // Reports the failure and lets it through: the modal keeps what was typed only if it hears
  // that the save failed, and the adviser would otherwise have to retype the whole row.
  const handleSave = async (expenditure: Omit<EditableExpenditure, 'id' | 'clientId'>) => {
    try {
      await onSave([{ ...expenditure, id: selectedExpenditure?.id } as EditableExpenditure]);
    } catch (error) {
      showError('Failed to save expenditure');
      throw error;
    }
    showSuccess('Expenditure saved successfully');
    closeModal();
    setSelectedExpenditure(undefined);
  };

  const handleEdit = (expenditure: Expenditure) => {
    setSelectedExpenditure(expenditure);
    openModal();
  };

  const handleAdd = () => {
    setSelectedExpenditure(undefined);
    openModal();
  };

  const handleDelete = async (expenditure: Expenditure) => {
    if (!expenditure.id) return;
    try {
      await onDelete([expenditure.id]);
      showSuccess('Expenditure deleted');
    } catch {
      showError('Failed to delete expenditure');
    }
  };

  const renderTableView = () => (
    <TableViewBase
      items={items}
      columns={[
        {
          key: 'description',
          header: 'Description',
          sortable: true,
          renderValue: (_value, item) => truncatedTextOrNotRecorded(item.description),
        },
        { key: 'category', header: 'Category', sortable: true },
        { key: 'frequency', header: 'Frequency', sortable: true },
        {
          key: 'amount',
          header: `Spend (${period})`,
          sortable: true,
          sortValueGetter: item => amountForPeriod(item.amount, item.frequency, period),
          renderValue: (_value, item) =>
            frequencyAmountCell(period, item.amount, item.frequency, currency),
          align: 'right',
        },
      ]}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <>
      <PanelBase<Expenditure>
        items={items}
        addButtonLabel="Add a new expenditure"
        renderTableView={renderTableView}
        onAdd={handleAdd}
      />
      <ExpenditureModal
        opened={modalOpened}
        onClose={closeModal}
        onSubmit={handleSave}
        initialValues={selectedExpenditure}
      />
    </>
  );
}
